import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import Button from '@/components/ui/button'
import Currency from '@/components/currency/Currency'
// import { CrossIcon } from 'common/components/icons'
import { i18n } from '@/locale/i18n'
import { formatNumber, normalizeNumericStr } from '@/util/localizeNumber'
import { roundToDecimalPlaces } from '@/util/helpers'
import { useCurrencies, useCurrentUser } from '@/hooks'
import Dropdown from '@/components/ui/dropdown'
import Thumbnail from '@/components/ui/thumbnail'
import constants from '@/constants'
import { WAGER_STATUS } from '@/constants/marketBackendEnums'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearWagerSelling,
  getWagerSelected,
  markWagerSelling,
  placeLimitOrder,
  resetBetHistory,
  selectBetStatusById,
  selectLastOrderError,
  selectLastOrderStatus,
  selectUserOrders,
} from '@/store/wagers'
import {
  clearSelectedLimitPrice,
  selectActiveLanguage,
  selectAllExchangeRate,
  selectExchangeRate,
  selectExchangeRateStatus,
  selectSelectedLimitPrice,
} from '@/store/app'
import { toastService } from '@/api/toast'
import { useModal } from '@/components/modal-provider/ModalProvider'
import EmailNotConfirmed from '@/components/modal-provider/global-modal/email-not-confirmed'

import PlaceBetInput from '../place-bet-input/PlaceBetInput'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { useNavigate } from 'react-router-dom'
import { InfoIcon } from '@/assets/icons'
import OverlayPanel from '@/components/OverlayPanel/OverlayPanel'
import Calendar from '@/components/ui/Calendar'
import CalendarSheet from '@/components/ui/Calendar/CalendarSheet'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import {
  calculateFeesForShares,
  computeMakerRebate,
  computeMatchableShares,
  computeNetAmountOnSellFromOrderBook,
  simulateOrderBookSellByShares,
} from '@/features/market/utils/orderBookCalculations'
import { convertFromCanonical, convertToCanonical } from '@/util/currencies'
import { selectOrderBookByOutcomeId, fetchOrderBook, fetchMarketWagersByMarketId } from '@/store/markets'
import { updateCurrentUserSettings } from '@/store/account'
import { getErrorInfo, handleOrderError } from '@/store/wagers'
import { marketMakerDocumentationUrl } from '@/urls/index'

const prefetchImage = (src) => {
  if (!src) return
  try {
    const img = new Image()
    img.decoding = 'async'
    img.src = src
  } catch {
    // ignore prefetch errors (e.g. SSR / older browsers)
  }
}

const SellPlaceBet = forwardRef(
  (
    {
      question,
      onToggle,
      quickbet = false,
      place,
      wager,
      onWagerAction,
      marketType = 'market',
      onChangeToLimitOrder = () => {},
      inMarketPage = false,
      onChangeOutcome,
      onOrderConflict = () => {},
      setActiveTab = () => {},
    },
    ref
  ) => {
    const exchangeRateAll = useSelector(selectAllExchangeRate())
    const exchangeRateStatus = useSelector(selectExchangeRateStatus())
    const locale = useSelector(selectActiveLanguage()) || 'en'
    const selectedLimitPrice = useSelector(selectSelectedLimitPrice)
    const lastOrderStatus = useSelector(selectLastOrderStatus())

    const textField = useRef()
    const dispatch = useDispatch()
    const user = useCurrentUser()
    const { showModal } = useModal()
    const [animationParent] = useAutoAnimate()
    // Track if we've initialized shares for the current wager and marketType
    const initializedWagerId = useRef(null)
    const initializedMarketType = useRef(null)

    // const [wager, setwager] = useState(wager)
    const orderBook = useSelector(selectOrderBookByOutcomeId(wager?.outcome?.id, wager?.currency, wager?.position))
    const userOrders = useSelector(selectUserOrders()) || []
    // Calculate available shares by subtracting open order shares
    const availableShares = React.useMemo(() => {
      if (!wager) return 0

      // Filter for matching ask orders
      const matchingOrders = userOrders.filter(
        order =>
          order.side === 'ask' &&
          order.currency === wager.currency &&
          order.position === wager.position &&
          order.outcome === wager.outcome?.id
      )
      // Sum up the shares from matching orders
      const aggregatedOpenOrdersShares = matchingOrders.reduce((sum, order) => sum + (order.shares || 0), 0)

      // Return available shares (cannot be negative)
      return marketType === 'limit' ? Math.max(wager.shares - aggregatedOpenOrdersShares, 0) : wager.shares
    }, [wager, userOrders, marketType])

    const currencies = useCurrencies().currencies
    const currency = wager && currencies[wager?.currency]
    const exchangeRate = useSelector(
      selectExchangeRate(
        currency?.id === constants.REAL_MONEY_PLACEHOLDER ? 'USDC' : currency?.id,
        currency?.id === 'OOM' ? 'OOM' : question?.canonical_currency
      )
    )
    const sellStatus = useSelector(selectBetStatusById(wager?.id))
    const isSaving = sellStatus === constants.LOADING || lastOrderStatus === constants.LOADING
    const isMobileSize = useIsMobileSize()
    const navigate = useNavigate()
    const [errorId, setErrorId] = useState()
    const [partialShares, setPartialShares] = useState()
    const [amount, setAmount] = useState()
    // Input-specific error messages
    const [inputErrors, setInputErrors] = useState({})
    const lastOrderError = useSelector(selectLastOrderError())

    // Limit order specific states
    const [limitPrice, setLimitPrice] = useState('')
    const [expirationOption, setExpirationOption] = useState('until_cancelled')
    // For single date mode, store a Date object
    const [calendarDate, setCalendarDate] = useState({})
    const [calendarOpen, setCalendarOpen] = useState(false)
    // Add pricePerShare state for limit orders
    const [pricePerShare, setPricePerShare] = useState(0)
    const [matchedShares, setMatchedShares] = useState(null) // shares that will be matched at limit price

    // Use refs to track orderbook changes and prevent infinite loops
    const orderBookRef = useRef()
    const validationOrderBookRef = useRef()
    const matchedSharesOrderBookRef = useRef()

    const isOrderBookAvailable = Boolean(
      question?.order_book_enabled && orderBook && orderBook.sellSeries && orderBook.sellSeries.length > 0
    )

    const stableSellSeries = useMemo(() => {
      if (!orderBook?.sellSeries) return null
      return orderBook.sellSeries
    }, [orderBook?.sellSeries?.length, JSON.stringify(orderBook?.sellSeries)])

    // Market sell: one simulation for gross proceeds + fees (same walk as orderBookCalculations.simulateOrderBookSellByShares)
    const marketOrderBookSellFill = useMemo(() => {
      if (marketType !== 'market' || !question?.order_book_enabled || !stableSellSeries?.length || !currency) {
        return null
      }
      const sharesNum = Number(normalizeNumericStr(String(partialShares ?? ''), '.'))
      if (!sharesNum || sharesNum <= 0) return null
      const canonicalShares = convertToCanonical(sharesNum * currency.display_ratio, exchangeRate)
      return simulateOrderBookSellByShares(stableSellSeries, canonicalShares)
    }, [
      marketType,
      question?.order_book_enabled,
      stableSellSeries,
      partialShares,
      // `currency` is a fresh object on every render (useCurrencies rebuilds the map),
      // so depend on stable primitives to avoid recomputing this memo each render.
      wager?.currency,
      currency?.display_ratio,
      exchangeRate,
    ])

    const hasPositions =
      question &&
      question.wagers &&
      question.wagers.filter(w => {
        if (w.status !== WAGER_STATUS.PURCHASE) return false

        // Calculate available shares for this wager
        const matchingOrders = userOrders.filter(
          order =>
            order.side === 'ask' &&
            order.currency === w.currency &&
            order.position === w.position &&
            (order.outcome_id === w.outcome?.id || order.outcome?.id === w.outcome?.id)
        )
        const aggregatedOpenOrdersShares = matchingOrders.reduce((sum, order) => sum + (order.shares || 0), 0)
        const availableShares = marketType === 'limit' ? Math.max(w.shares - aggregatedOpenOrdersShares, 0) : w.shares

        return availableShares > 0
      }).length > 0

    const convertToUsd = _value => {
      if (exchangeRateStatus === constants.LOADING || !wager) return 0

      if (isNaN(_value)) return formatNumber(0, locale, 2, 8)
      const inUsd = 1 / exchangeRateAll.USD.rates[wager.currency]
      return inUsd * _value
    }

    // Get the maximum amount from orderbook for error display
    const maxOrderbookShares =
      isOrderBookAvailable && orderBook?.sellSeries?.length > 0
        ? Math.max(...orderBook.sellSeries.map(order => order.cumulative_shares))
        : null

    const maxOrderbookSharesFormatted = maxOrderbookShares
      ? convertFromCanonical(maxOrderbookShares, exchangeRate) / currency?.display_ratio
      : null

    const errorTypes = {
      // Error when user types a value higher than the maximum available
      select_wager: i18n('place_bet.outcome_not_selected'),
      amount_beyond_max: <React.Fragment>{i18n('place_bet.beyond_max_shares')}</React.Fragment>,
      amount_beyond_min: <React.Fragment>{i18n('place_bet.below_min_shares')}</React.Fragment>,
      limit_price_too_high: <React.Fragment>{i18n('place_bet.limit_price_too_high')}</React.Fragment>,
      no_orderbook_orders: i18n('place_bet.no_orders_available'),
      amount_beyond_orderbook_max: i18n('place_bet.amount_beyond_orderbook_max', {
        max: maxOrderbookSharesFormatted
          ? formatNumber(maxOrderbookSharesFormatted, locale, 0, currency?.display_decimal_places)
          : '0',
      }),
    }
    // hooks

    useImperativeHandle(ref, () => ({}))

    useEffect(() => {
      if (!isOrderBookAvailable || !partialShares || !wager || !currency) return

      const sellSeriesString = JSON.stringify(orderBook?.sellSeries || [])

      const calculationKey = JSON.stringify({
        sellSeries: sellSeriesString,
        partialShares,
        wagerId: wager?.id,
        currencyRatio: currency?.display_ratio,
        exchangeRate,
      })

      if (orderBookRef.current === calculationKey) return

      orderBookRef.current = calculationKey

      if (orderBook?.sellSeries?.length > 0) {
        const net = computeNetAmountOnSellFromOrderBook({
          sellSeries: orderBook.sellSeries,
          betShares: partialShares,
          currency,
          exchangeRateValue: exchangeRate,
        })
        if (net != null) setAmount(net)
      }
    }, [orderBook, partialShares, wager, currency, currency?.display_ratio, exchangeRate, isOrderBookAvailable])

    useEffect(() => {
      if (marketType === 'limit' || !currency) return

      if (question?.order_book_enabled && stableSellSeries && stableSellSeries.length === 0) {
        setPricePerShare(0)
        return
      }

      if (question?.order_book_enabled && stableSellSeries && stableSellSeries.length > 0) {
        if (!marketOrderBookSellFill) return

        const { grossAmountCanonical, sharesConsumed } = marketOrderBookSellFill

        if (sharesConsumed <= 0 || grossAmountCanonical <= 0) {
          setPricePerShare(0)
          return
        }

        const proceedsDisplay = convertFromCanonical(grossAmountCanonical, exchangeRate)
        const sharesFilledDisplay = convertFromCanonical(sharesConsumed, exchangeRate)
        const nextPricePerShare = Number((proceedsDisplay / sharesFilledDisplay).toFixed(2)) * currency.display_ratio
        if (Number.isFinite(nextPricePerShare)) {
          setPricePerShare(prev => (prev === nextPricePerShare ? prev : nextPricePerShare))
        }
      }
    }, [
      stableSellSeries,
      question?.order_book_enabled,
      // Depend on stable primitives instead of the per-render `currency` object.
      wager?.currency,
      currency?.display_ratio,
      exchangeRate,
      marketType,
      marketOrderBookSellFill,
    ])

    // Apply the price picked from the order book. Intentionally not keyed on the order book:
    // it refreshes in real time and would keep overwriting whatever the user typed.
    useEffect(() => {
      if (marketType !== 'limit' || selectedLimitPrice === undefined || selectedLimitPrice === null) return
      setLimitPrice(prev => (String(selectedLimitPrice) === String(prev) ? prev : String(selectedLimitPrice)))
    }, [marketType, selectedLimitPrice])

    // Seed an empty field with the best bid once the book arrives.
    useEffect(() => {
      if (marketType !== 'limit' || (selectedLimitPrice !== undefined && selectedLimitPrice !== null)) return
      setLimitPrice(prev => {
        if (prev !== '' && prev !== undefined) return prev
        if (stableSellSeries?.length > 0) return stableSellSeries[0].price.toFixed(2)
        return '0.50'
      })
    }, [marketType, selectedLimitPrice, stableSellSeries])

    // Shares of a limit sell that fill immediately: the bids priced at or above the limit price.
    useEffect(() => {
      if (marketType !== 'limit' || !limitPrice || !partialShares || !isOrderBookAvailable || !stableSellSeries) {
        setMatchedShares(null)
        return
      }

      const limitPriceNum = parseFloat(limitPrice)
      const sharesNum = parseFloat(partialShares)

      if (isNaN(limitPriceNum) || isNaN(sharesNum) || limitPriceNum <= 0 || sharesNum <= 0) {
        setMatchedShares(null)
        return
      }

      // A limit sell matches the bids priced at or above the limit price.
      const matchedSharesCanonical = computeMatchableShares(stableSellSeries, limitPriceNum, { side: 'ask' })

      if (matchedSharesCanonical === 0) {
        setMatchedShares(null)
        return
      }

      const matchedSharesDisplay =
        convertFromCanonical(matchedSharesCanonical, exchangeRate) / (currency?.display_ratio || 1)
      const finalMatchedShares = Math.min(sharesNum, matchedSharesDisplay)
      setMatchedShares(finalMatchedShares > 0 ? finalMatchedShares : null)
    }, [
      marketType,
      limitPrice,
      partialShares,
      isOrderBookAvailable,
      stableSellSeries,
      exchangeRate,
      currency?.display_ratio,
    ])

    // // ? check for errors for each interaction
    // useEffect(() => {
    //   if (question && question.wagers) {
    //     if (wager && question.wagers.find(w => w.status === WAGER_STATUS.PURCHASE && w.id === wager.id)) {
    //       setwager(wager)
    //     } else if (!wager && question.wagers.filter(w => w.status === WAGER_STATUS.PURCHASE).length > 0) {
    //       // Select the first wager if no wager is provided
    //       setwager(question.wagers.find(w => w.status === WAGER_STATUS.PURCHASE))
    //     } else if (!wager && question.wagers.filter(w => w.status === WAGER_STATUS.PURCHASE).length === 0) {
    //       setwager(null)
    //     }
    //   }
    // }, [wager, question])

    useEffect(() => {
      if (wager) checkValidInput()
    }, [partialShares, wager, limitPrice, marketType, availableShares, userOrders])

    // Prefetch thumbnails for sellable outcomes to reduce delay when opening selector
    useEffect(() => {
      const purchased = (question?.wagers || []).filter(w => w?.status === WAGER_STATUS.PURCHASE)
      const urls = purchased
        .slice(0, 20)
        .map(w => w?.outcome?.thumbnail)
        .filter(Boolean)
      urls.forEach(prefetchImage)
    }, [question?.id])

    // Non-stable real money: market sell only via order book; limit orders disabled in UI
    const isSellLimitDisabled =
      currency &&
      !currency.is_stable &&
      (currency.currency_mode === constants.REAL_MONEY || wager?.currency !== 'OOM')

    useEffect(() => {
      if (wager && currency) {
        // dispatch(getwager(wager))

        // Initialize shares when wager or marketType changes, not on orderbook updates
        if (initializedWagerId.current !== wager.id || initializedMarketType.current !== marketType) {
          // Calculate initial shares using min between full shares and available shares
          // but still respecting orderbook maximum
          let initialShares = Math.min(wager.shares, availableShares)

          // If orderbook is available, use the minimum between initial shares and orderbook max
          // (intentionally not in deps to avoid reset on orderbook updates)
          if (isOrderBookAvailable && maxOrderbookSharesFormatted !== null) {
            initialShares = Math.min(initialShares, maxOrderbookSharesFormatted)
          }
          setPartialShares(roundToDecimalPlaces(Number(initialShares), currency.display_decimal_places, 'floor'))
          initializedWagerId.current = wager.id
          initializedMarketType.current = marketType
        }
      } else if (!wager) {
        // Reset tracking when wager is cleared
        initializedWagerId.current = null
        initializedMarketType.current = null
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wager?.id, currency?.id, availableShares, marketType])

    // Auto-select first wager if none is selected
    useEffect(() => {
      if (!wager && question?.wagers) {
        const firstWager = question.wagers.find(w => w.status === WAGER_STATUS.PURCHASE)
        if (firstWager) {
          dispatch(getWagerSelected(firstWager))
        }
      }
    }, [wager, question?.wagers, dispatch])

    // Watch for lastOrderError changes and update inputErrors accordingly
    useEffect(() => {
      if (lastOrderError && lastOrderError.error && Array.isArray(lastOrderError.error.errors)) {
        const newInputErrors = {}
        lastOrderError.error.errors.forEach(e => {
          if (e.attr && e.detail) {
            newInputErrors[e.attr] = e.detail
          }
        })
        setInputErrors(newInputErrors)
      } else {
        setInputErrors({})
      }
    }, [lastOrderError])

    // Render the calendar modal for custom expiration date
    const renderCalendarContent = () => {
      return (
        <div className={isMobileSize ? 'grid justify-items-stretch' : 'grid justify-items-end'}>
          <Calendar
            fullWidth={isMobileSize}
            mode="single"
            value={calendarDate.selectedDate || new Date()}
            onChange={date => {
              setCalendarDate(dates => ({ ...dates, selectedDate: date }))
            }}
            minDate={new Date()}
            // Optionally set maxDate if needed
          />
          <Button
            onClick={() => {
              setCalendarDate(dates => ({ ...dates, confirmedDate: dates.selectedDate }))
              setCalendarOpen(false)
            }}
            className="my-4"
            variant="primary"
            size="large">
            {i18n('place_bet.confirm')}
          </Button>
        </div>
      )
    }

    // Helper function to handle order conflict errors
    const handleOrderConflictError = async error => {
      if (user?.settings?.auto_cancel_conflicting_orders) {
        // Automatically retry with cancel_conflicting_orders=true
        await onMakeSell(true)
        return true // Indicates error was handled and should not be re-thrown
      }

      // Call the order conflict callback
      onOrderConflict({
        onRetry: (cancelConflictingOrders, dontAskAgain) => {
          onFormSubmit(cancelConflictingOrders, dontAskAgain)
        },
      })
      return false // Indicates error should be re-thrown to prevent cleanup
    }

    // Helper function to handle invalid amount errors
    const handleInvalidAmountError = () => {
      if (!wager) return
      // Re-fetch the order book to get updated data
      dispatch(
        fetchOrderBook({
          question: question.id,
          currency_mode: wager.currency === 'OOM' ? 'play_money' : 'real_money',
          outcome: wager.outcome.id,
          position: wager.position,
          source: 'sell_place_bet',
          showErrorToast: true,
        })
      )
    }

    const onMakeSell = async (cancelConflictingOrders = false, dontAskAgain = false) => {
      if (!wager) return

      const onSuccess = () => {
        // Update user settings if they chose "don't ask again"
        if (cancelConflictingOrders && dontAskAgain) {
          dispatch(updateCurrentUserSettings({ auto_cancel_conflicting_orders: true }))
        }

        dispatch(clearSelectedLimitPrice())

        if (marketType === 'limit') {
          setCalendarDate({})
        }
        toastService('wagerBuy', {
          question,
          title: marketType === 'market' ? i18n('place_bet.sold') : i18n('place_bet.limit_order_placed'),
        })

        // Switch to buy tab after successful sell
        setActiveTab('buy')
      }
      const _partialShares =
        wager &&
        currency &&
        partialShares &&
        Math.abs(Number(partialShares) - availableShares) < Math.pow(10, -(currency?.display_decimal_places || 2))
          ? availableShares
          : partialShares

      const price = marketType === 'market' ? null : Number(limitPrice)

      let expirationDate = null
      if (expirationOption === 'custom' && calendarDate.confirmedDate) {
        expirationDate = new Date(calendarDate.confirmedDate)
      } else if (expirationOption === 'end_of_day') {
        // Set to end of today (23:59:59)
        const now = new Date()
        expirationDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
      }

      const side = 'ask'

      /* A market sale empties the position as soon as it goes through, but the
         market's wagers only catch up on the next refresh. Marking it here keeps it
         out of the card and the bet list meanwhile, so it can't be offered for sale
         again. A limit ask leaves the position open until it fills, so it stays. */
      const isMarketSell = marketType === 'market'
      if (isMarketSell) dispatch(markWagerSelling({ betId: wager.id }))

      try {
        await dispatch(
          placeLimitOrder(
            wager.outcome.id,
            _partialShares,
            price,
            wager.currency,
            wager.position,
            expirationOption,
            expirationDate,
            place,
            side,
            onSuccess,
            undefined, // amount parameter
            cancelConflictingOrders
          )
        )
      } catch (error) {
        if (isMarketSell) dispatch(clearWagerSelling({ betId: wager.id }))
        // Use centralized error handling from wagers.js
        const errorInfo = getErrorInfo(error)

        // Handle order conflict errors with async support
        if (errorInfo?.code === 'OrderBookConflictingOrders') {
          const handled = await handleOrderConflictError(error)
          if (handled) {
            return // Error was handled (auto-retry), don't throw
          }
          // If not auto-handled, will be handled by UI callback, so re-throw
          throw error
        }

        // Handle invalid amount error - toast is already shown in thunk, just handle side effects
        if (errorInfo?.code === 'OrderBookInvalidAmount') {
          handleInvalidAmountError()
          // Don't re-throw, error was handled (toast shown in thunk)
          return
        }

        // Use centralized handler for other errors (toast already handled in thunk)
        const result = handleOrderError(error, {
          showToast: false, // Toast is handled in thunk
        })

        // Determine if error should be re-thrown
        // If error was handled and shouldn't be re-thrown, return early
        if (result.handled && result.shouldRethrow === false) {
          return
        }

        // Re-throw to prevent cleanup in onFormSubmit
        throw error
      }

      if (isMarketSell) {
        await dispatch(fetchMarketWagersByMarketId(question.id, true))
        dispatch(clearWagerSelling({ betId: wager.id }))
      }

      await dispatch(resetBetHistory({ questionId: question.id }))

      // Auto close the widget if mobile or quickbet
      if (quickbet || isMobileSize) onToggle()

      // Reselect default outcome for desktop ViewMarketPage after sell
      if (inMarketPage && !isMobileSize && !quickbet && onChangeOutcome && question) {
        // Select the first outcome
        if (question.outcomes && question.outcomes.length >= 1) {
          onChangeOutcome(question.outcomes[0])
        }
      }
    }

    function checkValidInput() {
      let newError = ''

      if (!wager) {
        newError = 'select_wager'
        setErrorId(newError)
        return Boolean(newError)
      }

      if (marketType === 'market') {
        const _partialShares =
          wager &&
          currency &&
          Math.abs(partialShares - availableShares) < Math.pow(10, -(currency?.display_decimal_places || 2))
            ? availableShares
            : partialShares

        // Market sells require order book liquidity
        if (!question?.order_book_enabled) {
          newError = 'no_orderbook_orders'
        } else if (orderBook?.status !== constants.LOADING) {
          if (!orderBook?.sellSeries?.length) {
            newError = 'no_orderbook_orders'
          } else {
            const maxOrderbookShares = Math.max(...orderBook.sellSeries.map(order => order.cumulative_shares))
            const canonicalPartialShares = convertToCanonical(_partialShares * currency.display_ratio, exchangeRate)

            if (canonicalPartialShares > maxOrderbookShares) {
              newError = 'amount_beyond_orderbook_max'
            }
          }
        }

        if (!newError && Number(_partialShares) > availableShares) {
          newError = 'amount_beyond_max'
        } else if (!newError && Number(_partialShares) < 0) {
          newError = 'amount_beyond_min'
        }
      } else if (marketType === 'limit') {
        // Limit order validations
        if (parseFloat(limitPrice) > 0.99) {
          newError = 'limit_price_too_high'
        } else if (parseFloat(partialShares) > availableShares) {
          newError = 'amount_beyond_max'
        } else if (parseFloat(partialShares) <= 0) {
          newError = 'amount_beyond_min'
        }
      }

      setErrorId(newError)
      return Boolean(newError)
    }

    const onFormSubmit = async (cancelConflictingOrders = false, dontAskAgain = false) => {
      const err = checkValidInput()
      if (err) return

      // Don't proceed if there are orderbook errors that should show the limit order button
      if (errorId === 'no_orderbook_orders' || errorId === 'amount_beyond_orderbook_max') {
        return
      }

      // Check email confirmation for all users (both wallet and non-wallet users)
      if (!user.email_confirmed) {
        showModal({
          title: ' ',
          showCloseButton: true,
          content: <EmailNotConfirmed />,
        })
        return
      }

      // Check for order conflicts before submitting (only if not already retrying with cancelConflictingOrders)
      if (!cancelConflictingOrders) {
        // Calculate how many shares are committed to open orders
        const matchingOrders = userOrders.filter(
          order =>
            order.side === 'ask' &&
            order.currency === wager.currency &&
            order.position === wager.position &&
            order.outcome === wager.outcome?.id
        )
        const sharesInOpenOrders = matchingOrders.reduce((sum, order) => sum + (order.shares || 0), 0)
        const _partialShares =
          wager &&
          currency &&
          partialShares &&
          Math.abs(Number(partialShares) - availableShares) < Math.pow(10, -(currency?.display_decimal_places || 2))
            ? availableShares
            : partialShares
        // Only show conflict panel if there are actually open orders causing the shortage
        if (wager.shares - sharesInOpenOrders < Number(_partialShares)) {
          // Check if user has auto-cancel setting enabled
          if (user?.settings?.auto_cancel_conflicting_orders) {
            // Automatically proceed with cancel_conflicting_orders=true
            await onFormSubmit(true)
            return
          }

          onOrderConflict({
            onRetry: (cancelConflictingOrders, dontAskAgain) => {
              onFormSubmit(cancelConflictingOrders, dontAskAgain)
            },
          })
          return
        }
      }
      // TODO : uncomment this when the KYC is done
      // if (user.block_bets && wager.currency !== 'OOM') {
      //   showModal({
      //     title: ' ',
      //     showCloseButton: true,
      //     content: <RenderAdditionalInfos currentUser={user} />,
      //   })
      //   return
      // }

      if (!errorId) {
        textField.current && textField.current.blur()
        try {
          await onMakeSell(cancelConflictingOrders, dontAskAgain)
          // Only execute cleanup if onMakeSell was successful
          // setwager(null)
          // dispatch(getwager(null))
          if (onWagerAction) onWagerAction()
        } catch (error) {
          // onMakeSell threw an error (including order conflicts)
          // Don't execute cleanup code in this case
        }
      }
    }
    const isCryptoNotStable = currency && currency?.currency_type === 'crypto' && currency?.id?.indexOf('USD') === -1

    const renderNoPositionsState = () => {
      const allPositions = question?.wagers?.filter(w => w.status === WAGER_STATUS.PURCHASE) || []
      const hasPositionsButNoAvailable =
        allPositions.length > 0 &&
        allPositions.every(wager => {
          const matchingOrders = userOrders.filter(
            order =>
              order.side === 'ask' &&
              order.currency === wager.currency &&
              order.position === wager.position &&
              (order.outcome_id === wager.outcome?.id || order.outcome?.id === wager.outcome?.id)
          )
          const aggregatedOpenOrdersShares = matchingOrders.reduce((sum, order) => sum + (order.shares || 0), 0)
          return wager.shares - aggregatedOpenOrdersShares <= 0
        })

      return (
        <div className="flex flex-col items-center justify-center w-full gap-4 py-10">
          <i className="text-Neutral-4 text-2xl bg-surface-3 rounded-full h-12 w-12 flex items-center justify-center">
            <InfoIcon fillcolor="currentColor" width="16px" height="16px" />
          </i>
          <p className="text-[#4C626D] text-sm">
            {hasPositionsButNoAvailable
              ? i18n('place_bet.all_shares_committed_to_orders')
              : i18n('place_bet.no_open_positions')}
          </p>
        </div>
      )
    }

    const renderMatchedSharesChip = () => {
      if (errorId || matchedShares === null || matchedShares <= 0) return null

      return (
        <OverlayPanel
          content={
            <p className="text-sm text-Neutral-6 w-40">
              {formatNumber(matchedShares, locale, 2, currency?.display_decimal_places || 2)}{' '}
              {i18n('place_bet.shares_from_order_executed')}
            </p>
          }
          customPosition={{ top: '24px', right: '0px', left: 'auto' }}
          showMode={isMobileSize ? 'click' : 'hover'}
          hideOnClickOutside
          className="!px-4 !py-3 !rounded-[var(--radius-sm)] !min-w-fit max-w-10 shadow-xl border border-surface-3">
          <span className="cursor-pointer text-positive-4">
            {formatNumber(matchedShares, locale, 0, currency?.display_decimal_places || 2)}{' '}
            {i18n('place_bet.matching')}
          </span>
        </OverlayPanel>
      )
    }

    const renderLimitInputs = () => {
      const limitPriceError = errorId === 'limit_price_too_high'
      const sharesError = errorId === 'amount_beyond_max' || errorId === 'amount_beyond_min'

      return (
        <div>
          <div
            className={classNames(
              'rounded-[var(--radius-sm)] border border-solid border-Neutral-3 divide-y divide-Neutral-3 transition duration-200 focus-within:border-[var(--color-brand-04)]',
              { '!border-negative-4': limitPriceError || sharesError || Boolean(inputErrors.shares) }
            )}>
            <PlaceBetInput
              testId="place-bet-sell-limit-price-input"
              variant="row"
              disabled={!wager}
              value={limitPrice}
              onChange={value => {
                // Allow empty string or values that could potentially become <= 0.99
                if (
                  value === '' ||
                  value === '0' ||
                  value === '0.' ||
                  (parseFloat(value) <= 0.99 && !isNaN(parseFloat(value)))
                ) {
                  setLimitPrice(value)
                }
              }}
              decimalScale={2}
              mode="limit"
              label={i18n('place_bet.limit_price')}
              step={0.01}
              locale={locale}
              currencyId={wager?.currency}
              noCurrency={true}
            />

            <PlaceBetInput
              testId="place-bet-sell-shares-input"
              variant="stacked"
              disabled={!wager}
              value={partialShares}
              onChange={setPartialShares}
              decimalScale={currency?.display_decimal_places}
              mode="limit"
              label={i18n('place_bet.shares')}
              step={1}
              locale={locale}
              currencyId={wager?.currency}
              chipSet="shares"
              noCurrency={true}
              helperText={renderMatchedSharesChip()}
              onChipSelect={chipValue => {
                if (!currency) return
                const shares = chipValue === 'max' ? availableShares : availableShares * chipValue
                setPartialShares(roundToDecimalPlaces(shares, currency.display_decimal_places, 'floor'))
              }}
            />
          </div>

          {errorId && (
            <div className="mt-1 text-negative-4 text-xs" data-testid={`place-bet-error-${errorId}`}>
              {errorTypes[errorId]}
            </div>
          )}
          {inputErrors.shares && <div className="mt-1 text-negative-4 text-xs">{inputErrors.shares}</div>}
        </div>
      )
    }

    const renderExpirationRow = () => {
      const customLabel =
        expirationOption === 'custom' && calendarDate.confirmedDate
          ? new Date(calendarDate.confirmedDate).toLocaleDateString()
          : i18n('place_bet.custom')

      const expirationOptions = [
        { value: 'until_cancelled', text: i18n('place_bet.no_expiration') },
        { value: 'end_of_day', text: i18n('place_bet.end_of_day') },
        { value: 'custom', text: customLabel },
      ]

      return (
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-Neutral-6">{i18n('place_bet.expiration_date')}</span>
          <Dropdown
            dropdownType="list"
            value={expirationOption}
            list={expirationOptions}
            arrow="outlined"
            singleOptionStyle="tick"
            className="expiration-dropdown"
            ariaLabel={i18n('place_bet.expiration_date')}
            onChange={optionValue => {
              setExpirationOption(optionValue)
              if (optionValue === 'custom') setCalendarOpen(true)
            }}
          />
        </div>
      )
    }

    const renderPartialSellInput = () => {
      return (
        <>
          <PlaceBetInput
            testId="place-bet-sell-shares-input"
            disabled={!wager}
            value={partialShares}
            currencyId={wager?.currency}
            onChange={v => {
              if (!wager || !currency) return
              // Allow intermediate values like "10." to be typed
              if (typeof v === 'string' && (v.endsWith('.') || v === '')) {
                setPartialShares(v)
              } else {
                setPartialShares(roundToDecimalPlaces(v, currency.display_decimal_places))
              }
            }}
            decimalScale={currency?.display_decimal_places}
            hasError={Boolean(errorId)}
            locale={locale}
            chipSet="shares"
            suffix={' ' + i18n('place_bet.shares')}
            onChipSelect={chipValue => {
              if (!wager || !currency) return
              // A market sell can only fill what the order book can absorb.
              const maxShares =
                isOrderBookAvailable && maxOrderbookSharesFormatted !== null
                  ? Math.min(availableShares, maxOrderbookSharesFormatted)
                  : availableShares
              const shares = chipValue === 'max' ? maxShares : Math.min(availableShares * chipValue, maxShares)
              setPartialShares(roundToDecimalPlaces(shares, currency.display_decimal_places))
            }}
          />
          {errorId && <div className="mt-1 text-negative-4 text-xs" data-testid={`place-bet-error-${errorId}`}>{errorTypes[errorId]}</div>}
          {pricePerShare > 0 && (
            <div className="flex justify-between items-start gap-2 mt-1">
              <div className="text-xs text-Neutral-4">
                {i18n('place_bet.average_price')} {pricePerShare === 0.01 && '< '}
                {pricePerShare.toFixed(2)}
              </div>
               
            </div>
          )}
        </>
      )
    }
    const renderSellableOutcomeSelector = () => {
      let outcomeList
      if (question && (!question.wagers || question.wagers.filter(w => w.status === WAGER_STATUS.PURCHASE).length === 0))
        outcomeList = [
          {
            value: null,
            text: (
              <div className="flex flex-col justify-center items-center w-full gap-2">
                <span className="font-bold">No Positions purchased yet</span>
                <span className="!text-Neutral-4 text-xs">No Positions purchased yet</span>
              </div>
            ),
          },
        ]
      else {
        outcomeList = question.wagers
          .filter(w => w.status === WAGER_STATUS.PURCHASE)
          .map(({ outcome, ...wager }) => {
            // Calculate available shares for this specific wager
            const matchingOrders = userOrders.filter(
              order =>
                order.side === 'ask' &&
                order.currency === wager.currency &&
                order.position === wager.position &&
                order.outcome === outcome?.id
            )

            const aggregatedOpenOrdersShares = matchingOrders.reduce((sum, order) => sum + (order.shares || 0), 0)
            const wagerAvailableShares =
              marketType === 'limit' ? Math.max(wager.shares - aggregatedOpenOrdersShares, 0) : wager.shares

            const positionLabels = getPositionLabels(outcome, question)
            const positionLabel =
              wager.position === constants.position.LONG ? positionLabels.longLabel : positionLabels.shortLabel
            const positionColor =
              wager.position === constants.position.SHORT ? '!text-[#ED0071]' : '!text-[var(--color-brand-04)]'

            return {
              value: wager.id,
              wagerAvailableShares,
              text: (
                <div className="flex gap-4 items-center">
                  <Thumbnail
                    image={outcome.thumbnail}
                    alt={outcome.title}
                    className="rounded w-8 h-8"
                    variant="rect"
                  />
                  <div className="flex flex-col gap-2">
                    <div className="text-Neutral-6">
                      {(question.outcomes_count ?? question.outcomes?.length ?? 0) > 1 ? (
                        <>
                          <span className={positionColor}>{positionLabel}</span>
                          {' '}
                          {i18n('bet_card.on')}{' '}
                          <span className="inline-block max-w-full truncate align-bottom">{outcome.title}</span>
                        </>
                      ) : (
                        <span className={`inline-block max-w-full truncate align-bottom ${positionColor}`}>
                          {positionLabel}
                        </span>
                      )}
                    </div>
                    <span className="text-Neutral-4 text-xs font-normal leading-none">
                      {currencies[wager.currency]?.display_code || wager.currency} ·{' '}
                      {formatNumber(
                        wagerAvailableShares || 0,
                        locale,
                        currencies[wager.currency]?.display_decimal_places || 2,
                        currencies[wager.currency]?.display_decimal_places || 2
                      )}{' '}
                      {i18n('place_bet.shares')}
                    </span>
                  </div>
                </div>
              ),
            }
          })
      }

      return (
        <Dropdown
          type="outline"
          dropdownType="list"
          placeholder={i18n('place_bet.select_bet')}
          title={wager?.outcome?.title}
          value={wager?.id}
          list={outcomeList}
          arrow="outlined"
          singleOptionStyle="tick"
          className="market-filter"
          ariaLabel={i18n('place_bet.sell')}
          onChange={id => {
            const selectedWager = question?.wagers?.find(w => w.id === id)
            if (selectedWager) {
              dispatch(getWagerSelected(selectedWager))
              if (onChangeOutcome && selectedWager?.outcome) {
                onChangeOutcome(selectedWager.outcome)
              }
            }
          }}
        />
      )
    }

    // Market vs. Limit tab content
    const renderMainContent = () => {
      if (marketType === 'market') {
        return (
          <>
            {renderPartialSellInput()}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-center mt-4">
              {!question?.order_book_enabled && (
                <div className="col-span-full text-negative-4 text-xs flex items-start gap-2 mt-1">
                  <InfoIcon fillcolor="currentColor" width="16px" height="16px" />
                  <div>{i18n('place_bet.no_orderbook_enabled')}</div>
                </div>
              )}
              {(errorId === 'no_orderbook_orders' || errorId === 'amount_beyond_orderbook_max') && (
                <div className="col-span-full">
                  <div className="rounded-[var(--radius-sm)] bg-surface-3 p-4">
                    <div className="flex gap-2 items-center">
                      <InfoIcon fillcolor="currentColor" width="16px" height="16px" />
                      <div className="text-sm text-Neutral-6">
                        <div>{i18n('place_bet.no_market_sell_available')}</div>
                        <div className="text-xs text-Neutral-4 mt-1">
                          {i18n('place_bet.try_limit_order_instead')}
                          <button
                            className="ml-1 underline hover:text-Neutral-6"
                            onClick={() => {
                              if (!isSellLimitDisabled) {
                                onChangeToLimitOrder()
                              }
                            }}
                            disabled={isSellLimitDisabled}>
                            {i18n('place_bet.create_limit_order')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )
      } else if (marketType === 'limit') {
        return (
          <>
            {renderLimitInputs()}
            {renderExpirationRow()}
          </>
        )
      }
    }

    // Fee on the shares that fill now, plus the maker rebate on the shares that rest in the book.
    const limitFeeBreakdown = useMemo(() => {
      if (marketType !== 'limit' || !currency) return null

      const limitPriceNum = parseFloat(limitPrice) || 0
      const sharesNum = parseFloat(partialShares) || 0
      const matchedSharesNum = Math.max(0, parseFloat(matchedShares) || 0)
      if (limitPriceNum <= 0 || sharesNum <= 0) return null

      let fee = 0
      if (matchedSharesNum > 0 && stableSellSeries?.length) {
        const matchedCanonical = convertToCanonical(matchedSharesNum * currency.display_ratio, exchangeRate)
        const { totalFee } = calculateFeesForShares(stableSellSeries, matchedCanonical, {
          fillPriceDirection: 'desc',
        })
        fee = convertFromCanonical(totalFee, exchangeRate) / currency.display_ratio
      }

      const rebate = computeMakerRebate({
        shares: sharesNum,
        matchedShares: matchedSharesNum,
        price: limitPriceNum,
      })

      return { fee, rebate }
    }, [
      marketType,
      limitPrice,
      partialShares,
      matchedShares,
      stableSellSeries,
      currency?.display_ratio,
      exchangeRate,
    ])

    // Amount the user gets for the shares being sold, in display currency, net of fees.
    const proceeds = (() => {
      if (marketType !== 'limit') return Number(amount) || 0
      const gross = (parseFloat(limitPrice) || 0) * (parseFloat(partialShares) || 0)
      return Math.max(0, gross - (limitFeeBreakdown?.fee || 0))
    })()

    const renderProceedsInfo = () => {
      const showLimitFeeBreakdownTooltip = marketType === 'limit' && Boolean(limitFeeBreakdown)

      return (
      <div className="flex items-center justify-between gap-1">
        <span className="text-sm text-Neutral-4 flex items-center gap-1">
          <span className="whitespace-nowrap">{i18n('place_bet.min_to_receive')}</span>
          {showLimitFeeBreakdownTooltip && (
            <OverlayPanel
              content={
                <div className="min-w-[200px] space-y-3 text-sm text-Neutral-6">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-Neutral-4">{i18n('place_bet.fee')}</span>
                    <Currency
                      value={limitFeeBreakdown.fee || 0}
                      currency={wager?.currency}
                      showPrefix
                      decimalPlaces={currency?.display_decimal_places}
                      valueClassName="text-sm"
                      className="text-sm"
                      roundingMode="halfCeil"
                      noNumberFlow
                    />
                  </div>

                  {limitFeeBreakdown.rebate > 0 && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-Neutral-4">{i18n('place_bet.rebate')}</span>
                      <Currency
                        value={limitFeeBreakdown.rebate}
                        currency={wager?.currency}
                        showPrefix
                        decimalPlaces={currency?.display_decimal_places}
                        valueClassName="text-sm text-brand-gradient"
                        className="text-sm font-semibold"
                        roundingMode="halfCeil"
                        noNumberFlow
                      />
                    </div>
                  )}
                  <a
                    href={marketMakerDocumentationUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-1 text-xs text-center font-normal hover:underline">
                    {i18n('convert_to_stable_coin_modal.know_more')}
                  </a>
                </div>
              }
              customPosition={{ top: '-110px', right: 'auto', left: '10px' }}
              showMode={isMobileSize ? 'click' : 'hover'}
              hideOnClickOutside
              className="!px-4 !py-3 !rounded-[var(--radius-sm)] shadow-3xl max-w-xs break-words">
              <InfoIcon className="cursor-pointer" width="14px" height="14px" />
            </OverlayPanel>
          )}
        </span>
        {wager ? (
          <>
            <Currency
              value={proceeds}
              currency={wager.currency}
              showPrefix
              valueClassName="text-positive-4 text-2xl"
              className="font-semibold leading-snug"
              decimalPlaces={currency?.display_decimal_places}
              roundingMode="halfCeil"
              noNumberFlow
            />
            {isCryptoNotStable && (
              <div className="text-xs text-Neutral-4">
                {'≈  $ '} {formatNumber(Number(convertToUsd(proceeds)), locale, 0, 2)}
              </div>
            )}
          </>
        ) : (
          '-'
        )}
      </div>
      )
    }

    const renderSubmitButton = () => {
      // Market sells with no order book liquidity offer the limit-order path instead.
      if (
        marketType === 'market' &&
        (errorId === 'no_orderbook_orders' || errorId === 'amount_beyond_orderbook_max')
      ) {
        return null
      }

      return (
        <Button
          data-testid="place-bet-submit-button"
          data-action="sell"
          onClick={() => onFormSubmit()}
          disabled={isSubmitDisabled}
          loading={isSaving}
          variant="primary"
          type="solid"
          size="large"
          className={classNames(
            'h-12 font-bold rounded-[var(--radius-sm)] transition-colors',
            '!bg-brand-4 !hover:bg-brand-4/90 !text-[var(--color-contrast-01)]',
            { 'opacity-50 !cursor-not-allowed': isSubmitDisabled }
          )}>
          {i18n('place_bet.sell')}
        </Button>
      )
    }

    const isSubmitDisabled = Boolean(errorId) || isSaving || !wager

    return (
      <div className="flex flex-col w-full rounded-[var(--radius-sm)]">
        <div className="space-y-3 !p-5" ref={animationParent}>
          {hasPositions && place !== 'QuickBet' && renderSellableOutcomeSelector()}
          {hasPositions ? renderMainContent() : renderNoPositionsState()}
        </div>

        {hasPositions && (
          <div className="flex flex-col gap-3 !px-5 !pb-4">
            {renderProceedsInfo()}
            {renderSubmitButton()}
          </div>
        )}

        <CalendarSheet
          isOpen={calendarOpen}
          onClose={() => setCalendarOpen(false)}
          title={i18n('place_bet.select_expiration_date')}
          modalProps={{ position: 'center' }}>
          {renderCalendarContent()}
        </CalendarSheet>
      </div>
    )
  }
)

SellPlaceBet.displayName = 'SellPlaceBet'

export default SellPlaceBet
